import React from 'react';

import {
  AchievementContent,
  AchievementContext
} from '../contexts/achievement';
import {
  NotificationContent,
  NotificationContext
} from '../contexts/notification';
import { PlayerContext } from '../contexts/player';
import { GameDocumentContext } from '../contexts/game-document';
import { GetItemAchievement } from '../utils/game-engine/inventory';
import { DisplayLanguageContext } from '../contexts/display-languages';
import { generateTitleById } from '../utils/game-document/display-languages';
import { GetTitleAchievement } from '../features/in-game/overlay/drawer/tabs/roles';

export const useGameToast = () => {
  const [player] = React.useContext(PlayerContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [achievement, setAchievement] = React.useContext(AchievementContext);
  const [notification, setNotification] = React.useContext(NotificationContext);

  const playerState = player.playerState;

  const addItemToast = (
    itemId: string,
    quantity: number,
    isTeam: boolean = false
  ) => {
    const itemCard = GetItemAchievement(
      gameDocument.gameDocument!,
      playerState?.language?.name!,
      itemId,
      quantity
    );

    if (itemCard.hideInGame) return;

    const addItemNotification: AchievementContent = {
      isHide: false,
      imageUrl: itemCard.imageRes,
      message: (
        <>
          <h1>{itemCard.quantity}</h1>
          <h2>{itemCard.titleRes}</h2>
          <p>{itemCard.summaryRes}</p>
        </>
      )
    };

    const content: AchievementContent[] = achievement.content;
    content.push(addItemNotification);
    setAchievement({ ...achievement, content });

    const addItemAlertNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>
            {quantity} {itemCard.titleRes} {isTeam ? 'Team' : ''} items added to
            your Inventory
          </strong>
        </span>
      )
    };

    setNotification((prev) => ({
      ...prev,
      content: [...prev.content, addItemAlertNotification]
    }));
  };

  const removeItemToast = (
    itemId: string,
    quantity: number,
    isTeam: boolean = false
  ) => {
    const itemCard = GetItemAchievement(
      gameDocument.gameDocument!,
      playerState?.language?.name!,
      itemId,
      quantity
    );

    if (itemCard.hideInGame) return;

    const removeItemNotification: NotificationContent = {
      icon: 'warning',
      isHide: false,
      message: (
        <span>
          <strong>
            {quantity} {itemCard.titleRes} {isTeam ? 'Team' : ''} items removed
            from your Inventory
          </strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(removeItemNotification);
    setNotification({ ...notification, content });
  };

  const addScoreToast = (quantity: number, isTeam: boolean = false) => {
    const addScoreNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>
            {(
              generateTitleById(
                '88a6f4c3-09f5-4a93-89de-62f1d6c13938',
                gameDocument!,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || "Congratulations. You've been awarded % points."
            ).replace('%', quantity.toString())}
          </strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(addScoreNotification);
    setNotification({ ...notification, content });
  };

  const removeScoreToast = (quantity: number, isTeam: boolean = false) => {
    const removeScoreNotification: NotificationContent = {
      icon: 'warning',
      isHide: false,
      message: (
        <span>
          <strong>
            {quantity} {isTeam ? 'Team' : ''} points removed from your
          </strong>{' '}
          <strong>submission</strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(removeScoreNotification);
    setNotification({ ...notification, content });
  };

  const addTitleToast = (titleId: string, isTeam: boolean = false) => {
    const titleCard = GetTitleAchievement(
      gameDocument.gameDocument!,
      playerState?.language?.name!,
      titleId
    );

    if (!titleCard.hideInGame) {
      const removeItemNotification: AchievementContent = {
        isHide: false,
        imageUrl: titleCard.imageRes,
        message: (
          <>
            <h1>{isTeam ? 'Team' : ''} Role</h1>
            <h2>{titleCard.titleRes}</h2>
            <p>{titleCard.summaryRes}</p>
          </>
        )
      };
      const content: AchievementContent[] = achievement.content;
      content.push(removeItemNotification);
      setAchievement({ ...achievement, content });

      const addTitleAlertNotification: NotificationContent = {
        icon: 'notifications',
        isHide: false,
        message: (
          <span>
            <strong>
              {titleCard.titleRes} {isTeam ? 'Team' : ''} titles has been added
            </strong>
          </span>
        )
      };

      setNotification((prev) => ({
        ...prev,
        content: [...prev.content, addTitleAlertNotification]
      }));
    }
  };

  const removeTitleToast = (titleId: string, isTeam: boolean = false) => {
    const titleCard = GetTitleAchievement(
      gameDocument.gameDocument!,
      playerState?.language?.name!,
      titleId
    );
    const removeTitleNotification: NotificationContent = {
      icon: 'warning',
      isHide: false,
      message: (
        <span>
          <strong>
            {isTeam ? 'Team role' : ''} {titleCard.titleRes} has been removed
          </strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(removeTitleNotification);
    setNotification({ ...notification, content });
  };

  const addContentToast = (
    contentString: string,
    onCloseCallback: () => void
  ) => {
    const removeItemNotification: AchievementContent = {
      isHide: false,
      message: (
        <div
          className={'push-content'}
          dangerouslySetInnerHTML={{ __html: `${contentString}` }}></div>
      ),
      onCloseCallback
    };
    const content: AchievementContent[] = achievement.content;
    content.push(removeItemNotification);
    setAchievement({ ...achievement, content });
  };

  const showScoreNotification = (awardedScore: number) => {
    const content: NotificationContent[] = notification.content;
    // set message & icon type toast notification
    let message: string = '';

    /**
     * to display the score in the notification.
     * Make sure the translated results have a % sign, so the value can be replaced with a dynamic value.
     * There may be a human error when filling in the display language administrator
     */

    const pointAwardedTitle =
      generateTitleById(
        'cea1d18b-4808-4c1f-8d46-88edf44ba525',
        gameDocument,
        displayLanguageContext.displayLanguageSelected.resources!,
        'game'
      ) || 'points awarded for your correct answers!';

    message = `${awardedScore} ${pointAwardedTitle}`;

    const scoreNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message
    };
    content.push(scoreNotification);

    setNotification({
      ...notification,
      content
    });
  };

  return {
    addItemToast,
    addScoreToast,
    addTitleToast,
    removeItemToast,
    addContentToast,
    removeScoreToast,
    removeTitleToast,
    showScoreNotification
  };
};
